import { useCallback, useState } from "react";
import type React from "react";

/**
 * Batch Actions state for the Song Library (owner QA 2026-07-21: after a bulk
 * delete, the Batch Actions dropdown stayed stuck on "Delete Selected" and a
 * second bulk operation required a full browser refresh).
 *
 * Root cause of that bug: the dropdown is an antd controlled <Select>, and
 * `value={undefined}` makes antd treat the component as UNCONTROLLED — the
 * internally latched 'delete' value survives every state "reset", keeps the
 * stale label, and swallows the next identical selection (antd only fires
 * onChange when the value actually changes). The action value here is
 * therefore `BatchActionValue | null` — never undefined — and `null` is what
 * gets passed to the Select, which antd clears back to the placeholder.
 *
 * Every completed, failed, or cancelled bulk lifecycle operation must call
 * `resetBatchUi()` so the next batch can start immediately without a refresh.
 */

export type BatchActionValue = "archive" | "restore" | "delete";

export type SelectionLifecycle = "none" | "mixed" | "active" | "archived";

export interface ChooseBatchActionCallbacks {
  /** Mixed Active+Archived selection — warn and stay neutral. */
  onMixedSelection: () => void;
  /**
   * Permanent delete goes straight to the MDL preflight/confirm modal (the
   * single confirmation experience) — the dropdown never latches 'delete'.
   */
  onDelete: (ids: string[]) => void;
  /**
   * Status-scoped ids for a mixed Active+Archived selection (owner-approved
   * 2026-08-11). When provided, a mixed selection offers scoped actions —
   * Archive applies to the Active subset; Restore/Delete apply to the
   * Archived subset — instead of blocking with onMixedSelection.
   */
  mixedScope?: {
    activeIds: string[];
    archivedIds: string[];
  };
}

export function useBatchActions() {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [batchAction, setBatchAction] = useState<BatchActionValue | null>(null);
  const [batchModalOpen, setBatchModalOpen] = useState(false);

  /**
   * Full neutral reset: no selection, placeholder "Batch Actions", no
   * pre-confirm modal. Called after every bulk archive/restore/delete
   * (success, partial failure, all-failed) and on cancelled confirmations.
   */
  const resetBatchUi = useCallback(() => {
    setSelectedRowKeys([]);
    setBatchAction(null);
    setBatchModalOpen(false);
  }, []);

  /** Clear only the action value (e.g. filter reset keeps row selection). */
  const clearBatchAction = useCallback(() => {
    setBatchAction(null);
    setBatchModalOpen(false);
  }, []);

  const chooseBatchAction = useCallback(
    (
      value: BatchActionValue,
      selectionLifecycle: SelectionLifecycle,
      callbacks: ChooseBatchActionCallbacks,
    ) => {
      if (selectedRowKeys.length === 0) return;
      if (selectionLifecycle === "mixed") {
        const scope = callbacks.mixedScope;
        if (!scope) {
          // Legacy behavior (Artists / Albums / Record Labels): stay neutral.
          callbacks.onMixedSelection();
          setBatchAction(null);
          return;
        }
        const scopedIds =
          value === "archive" ? scope.activeIds : scope.archivedIds;
        if (scopedIds.length === 0) {
          callbacks.onMixedSelection();
          setBatchAction(null);
          return;
        }
        if (value === "delete") {
          setBatchAction(null);
          callbacks.onDelete(scopedIds.map(String));
          return;
        }
        setBatchAction(value);
        setBatchModalOpen(true);
        return;
      }
      if (value === "delete") {
        setBatchAction(null);
        callbacks.onDelete(selectedRowKeys.map(String));
        return;
      }
      setBatchAction(value);
      setBatchModalOpen(true);
    },
    [selectedRowKeys],
  );

  return {
    selectedRowKeys,
    setSelectedRowKeys,
    batchAction,
    batchModalOpen,
    chooseBatchAction,
    resetBatchUi,
    clearBatchAction,
  };
}
